/**
 * src/features/printing/components/KitchenPrinterPickerDialog.tsx
 *
 * Dialog modal pour sélectionner une imprimante cible avant impression KOT.
 * Liste les imprimantes du tenant filtrées par types (kitchen + bar par défaut).
 *
 * Invariants :
 *   4.B.2 — pas de supabase.* direct : passe par useTenantPrinters (qui passe par PrintingApi)
 *   4.B.4 — 0 any
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Loader2, Printer, AlertTriangle, Settings } from 'lucide-react';
import { useTenantPrinters } from '../hooks/useTenantPrinters';
import type { PrinterDevice, PrinterType } from '@/api/printing';

export type KitchenPrinterPickerDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Appelé quand l'utilisateur confirme la sélection.
   * Retourne l'id de l'imprimante choisie (jamais null si bouton activé).
   */
  onConfirm: (printerId: string, printer: PrinterDevice) => void;
  /**
   * Filtre des types proposés. Défaut : ['kitchen', 'bar'].
   */
  types?: PrinterType[];
  /**
   * Titre custom (ex: 'Imprimer KOT — Table 12').
   */
  title?: string;
  /**
   * URL admin imprimantes pour le CTA "configurer" si aucune imprimante.
   */
  adminUrl?: string;
};

const DEFAULT_TYPES: PrinterType[] = ['kitchen', 'bar'];

function connectionLabel(connectionType: PrinterDevice['connectionType']): string {
  switch (connectionType) {
    case 'sunmi':
      return 'Sunmi (intégrée)';
    case 'bluetooth':
      return 'Bluetooth';
    case 'network':
      return 'Réseau';
    case 'usb':
      return 'USB';
    default:
      return connectionType;
  }
}

function typeLabel(printerType: PrinterDevice['printerType']): string {
  switch (printerType) {
    case 'kitchen':
      return 'Cuisine';
    case 'bar':
      return 'Bar';
    case 'receipt':
      return 'Caisse';
    case 'label':
      return 'Étiquettes';
    default:
      return printerType;
  }
}

export function KitchenPrinterPickerDialog({
  open,
  onOpenChange,
  onConfirm,
  types = DEFAULT_TYPES,
  title = 'Choisir une imprimante',
  adminUrl = '/admin/imprimantes',
}: KitchenPrinterPickerDialogProps) {
  const { printers, isLoading, isError, isEmpty } = useTenantPrinters({
    types,
    enabled: open,
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Pré-sélection : imprimante par défaut, sinon première, sinon null.
  useEffect(() => {
    if (!open) return;
    const defaultPrinter = printers.find((p) => p.isDefault);
    if (defaultPrinter) {
      setSelectedId(defaultPrinter.id);
    } else if (printers.length > 0) {
      setSelectedId(printers[0].id);
    } else {
      setSelectedId(null);
    }
  }, [open, printers]);

  const handleConfirm = () => {
    if (!selectedId) return;
    const printer = printers.find((p) => p.id === selectedId);
    if (!printer) return;
    onConfirm(selectedId, printer);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="h-5 w-5" />
            {title}
          </DialogTitle>
          <DialogDescription>
            Sélectionnez l'imprimante cible pour ce bon de commande.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          {isLoading && (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Chargement…
            </div>
          )}

          {isError && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>Erreur de chargement des imprimantes. Vérifiez votre connexion.</span>
            </div>
          )}

          {!isLoading && !isError && isEmpty && (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <Settings className="h-10 w-10 text-muted-foreground" />
              <div className="text-sm text-muted-foreground">
                Aucune imprimante {types.join('/')} configurée pour ce point de vente.
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onOpenChange(false);
                  window.location.assign(adminUrl);
                }}
              >
                Configurer une imprimante
              </Button>
            </div>
          )}

          {!isLoading && !isError && !isEmpty && (
            <RadioGroup
              value={selectedId ?? ''}
              onValueChange={(v) => setSelectedId(v)}
              className="max-h-72 space-y-2 overflow-y-auto"
            >
              {printers.map((printer) => (
                <Label
                  key={printer.id}
                  htmlFor={`printer-${printer.id}`}
                  className="flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors hover:bg-accent"
                >
                  <RadioGroupItem value={printer.id} id={`printer-${printer.id}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium">{printer.name}</span>
                      {printer.isDefault && (
                        <Badge variant="secondary" className="text-xs">
                          Défaut
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-xs">
                        {typeLabel(printer.printerType)}
                      </Badge>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {connectionLabel(printer.connectionType)}
                      {printer.ipAddress && ` · ${printer.ipAddress}:${printer.port}`}
                      {printer.btMacAddress && ` · ${printer.btMacAddress.slice(0, 8)}…`}
                    </div>
                  </div>
                </Label>
              ))}
            </RadioGroup>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading || isEmpty || isError || !selectedId}>
            <Printer className="mr-2 h-4 w-4" />
            Imprimer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default KitchenPrinterPickerDialog;
